import { t } from "i18next";
import useMeaQuery from "qpr-base/apihooks/useMeaQuery";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";
import useFormStyles from "./KPIsForm/themes";
import FormikInputField from "./form/FormikInputField";
import FormikSelect from "./form/FormikSelect";

const BasicInfoForm = ({
  isProject,
  shortCode,
  DB2030,
  ...props
}: {
  isProject?: boolean;
  shortCode?: string;
  DB2030?: string;
  setField?: any;
  disabled?: boolean;
}) => {
  const ctx = useAppContext();
  const classes = useFormStyles();

  const query = `[${ctx.fullModelId}].BySymbol(symbol="KPI")`;
  const attributes = 'value(series="JS_ATTR",period="firstforseries")';

  const { data } = useMeaQuery<any[]>(query, attributes);
  const optionsString = data?.data[0]?.value || "[]";
  const optionsJSON = JSON.parse(optionsString);
  const options = React.useMemo(
    () =>
      [optionsJSON || []].map((option: any) => {
        const kpiType =
          option?.["Localized-Text-Attributes"]?.[ctx.rtl ? "ar" : "en-gb"]
            ?.Name;
        return {
          title: kpiType,
          value: option?.["Localized-Text-Attributes"]?.["en-gb"]?.Name,
        };
      }),
    [optionsJSON]
  );

  const handleChange = (value: string, field: string, arabicField: string) => {
    const arabicValue = optionsJSON.find(
      (option: any) =>
        option["Localized-Text-Attributes"]["en-gb"].Name === value
    )?.["Localized-Text-Attributes"].ar.Name;
    props.setField(field, value);
    props.setField(arabicField, arabicValue);
  };

  type Field = {
    name: string;
    label: string;
    placeholder: string;
    rows: number;
    multiline: boolean;
    withId?: boolean;
    type?: string;
  };

  const lang: string[] = conf.getLanguages();

  const firstLanguageFields: Field[] = [
    {
      name: `basicInformation[${lang[0]}].Name`,
      label: `${
        isProject ? t("initiatives.Project Name") : t("kpis.KPI Name")
      } (${t(lang[0])})`,
      placeholder: "", //isProject ? "Enter Project Name" : "Enter KPI Name",
      rows: 1,
      multiline: false,
    },
    {
      name: `basicInformation[${lang[0]}].Description`,
      label: `${
        isProject ? t("initiatives.Description") : t("kpis.Description")
      } (${t(lang[0])})`,
      placeholder: "", // "Enter Description",
      rows: 4,
      multiline: true,
    },
    ...[],
  ];

  const secondLanguageFields: Field[] = [
    {
      name: `basicInformation[${lang[1]}].Name`,
      label: `${
        isProject ? t("initiatives.Project Name") : t("kpis.KPI Name")
      } (${t(lang[1])})`,
      placeholder: "", // isProject ? "Enter Project Name" : "Enter KPI Name",
      rows: 1,
      multiline: false,
    },
    {
      name: `basicInformation[${lang[1]}].Description`,
      label: `${
        isProject ? t("initiatives.Description") : t("kpis.Description")
      } (${t(lang[1])})`,
      placeholder: "", // "Enter Description",
      rows: 4,
      multiline: true,
    },
    ...[],
  ];

  const colUI = (fields: Field[], rtl: boolean) => {
    return (
      <div className={classes.formColumn}>
        {fields.map((field, index) => {
          const fieldName = field.name;
          if (field.type === "select")
            return (
              <FormikSelect
                name={field.name}
                options={options}
                label={field.label}
                placeholder={field.placeholder}
                handleFieldChange={(value, field) =>
                  handleChange(value, field, fieldName.replace("en", "ar"))
                }
                {...props}
              />
            );
          if (field.withId)
            return (
              <div className={classes.flexRow}>
                <FormikInputField
                  key={index}
                  name={field.name}
                  label={field.label}
                  placeholder={field.placeholder}
                  multiline={field.multiline}
                  rows={field.rows}
                  {...props}
                  dir={ctx.rtl ? "rtl" : "ltr"}
                />
              </div>
            );
          return (
            <FormikInputField
              key={index}
              name={field.name}
              label={field.label}
              placeholder={field.placeholder}
              multiline={field.multiline}
              rows={field.rows}
              {...props}
              dir={ctx.rtl ? "rtl" : "ltr"}
            />
          );
        })}
      </div>
    );
  };

  return (
    <>
      <h1 className={classes.formTitle}>{t("Basic Information")}</h1>
      <div className={classes.formWrapper}>
        {ctx.rtl ? (
          <>
            {colUI(secondLanguageFields, lang[1] === "ar")}
            {colUI(firstLanguageFields, lang[0] === "ar")}
          </>
        ) : (
          <>
            {colUI(firstLanguageFields, lang[0] === "ar")}
            {colUI(secondLanguageFields, lang[1] === "ar")}
          </>
        )}
      </div>
    </>
  );
};
export default BasicInfoForm;
